/** Shared utilities for {@link TutorRow}, {@link TutorCard} and {@link TutorsDataTable}. */

import { Clock, Mail, Phone, type LucideIcon } from 'lucide-react';
import type { OnboardingFilters, TutorRecord, TutorSituation } from '@/types/remote/tutor.types';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import {
  ACTIVITY_SECTOR_LABEL,
  CIVIL_SERVICE_BRANCH_LABEL,
  FIELD_OF_ACTIVITY_TUTOR_LABEL,
  HIGH_SCHOOL_CURRICULUM_LABEL,
  STUDY_LEVEL_LABEL,
  TUTOR_PENDING_STATUS_CONFIG,
  TUTOR_ACTIVE_STATUS_CONFIG,
  type TutorRemoteFilters,
  type TutorRemoteTabView,
  type TutorStatusConfig,
  TUTOR_REMOTE_TAB_USER_STATUS,
  TUTOR_SITUATION_CONFIG,
} from '@/components/tutors/tutor-configs';
import { isReactivatableUserStatus, type WeeklyAvailabilitySlot } from '@/types/user.types';
import { resolveUserContacts } from '@/utils/user-contact-utils';
import { getTutorRemoteBusinessStatus, getTutorSituation } from '@/utils/common-utils';

export {
  completionBarColor,
  getCivilTitleLabel,
  getCompletionStatus,
  getCoursePathLabel,
  getSupportTypeLabel,
  getTutorRemoteBusinessStatus,
  getTutorRemoteMilestoneStatus,
  getTutorRemoteStatusFilterKeys,
  getTutorSituation,
} from '@/utils/common-utils';

/** RBAC actions available on a tutor row/card. Single source of truth. */
export interface TutorRowPageActions {
  canEditRow?: boolean;
  canViewRow?: boolean;
  canViewSessions?: boolean;
  canViewReports?: boolean;
  canArchiveRow?: boolean;
  canReactivateRow?: boolean;
}

/** Snapshot passed to {@link ArchiveTutorModal} from the list row. */
export interface TutorArchiveTarget {
  userId: string;
  tutorName: string;
  isTutoringInProgress: boolean;
}

/** Extended actions for {@link TutorsDataTable} (adds {@link DataTable}-level permissions). */
export interface TutorDataTablePageActions extends TutorRowPageActions {
  canExportCsv?: boolean;
}

/** Status + situation badge configs derived from a tutor record. */
export interface TutorDerivedStatus {
  statusConfig: TutorStatusConfig | undefined;
  StatusIcon: LucideIcon;
  situationConfig: TutorStatusConfig;
  SituationIcon: LucideIcon;
}

/** Primary contact icon + value, with optional secondary shown under it. */
export interface TutorPrimaryContact {
  icon: LucideIcon;
  value: string;
  secondaryValue: string | null;
}

/** One paired student from tutor binomes. */
type TutorPairPartner = NonNullable<NonNullable<TutorRecord['user']>['binomes']>[number];

/** List row id, or a user id already passed in, for the full-state GET. */
export function resolveTutorExpandedUserId(
  expandedRowId: string | null | undefined,
  rows: readonly TutorRecord[],
): string | undefined {
  if (!expandedRowId) return undefined;
  const foundById = rows.find((row) => row.id === expandedRowId);
  if (foundById) return foundById.userId;
  const foundByUserId = rows.find((row) => row.userId === expandedRowId);
  return foundByUserId?.userId ?? expandedRowId;
}

/** First + last name, or a generic fallback when the list row has no user. */
export function formatTutorDisplayName(record: TutorRecord | undefined): string {
  const name = `${record?.user?.firstName ?? ''} ${record?.user?.lastName ?? ''}`.trim();
  return name || 'Ce tuteur';
}

/** True when the situation badge is an active tutoring engagement. */
export function isTutorTutoringInProgress(record: TutorRecord | undefined): boolean {
  if (!record) return false;
  return (
    getTutorSituation({
      onboardingStatus: record.onboardingStatus,
      tutorSituation: record.tutorSituation,
      _binomeCount: record.matchedStudents?.length,
    }) === 'tutoring_in_progress'
  );
}

/** Build the archive modal target from the list row (name + situation). */
export function buildTutorArchiveTarget(
  record: TutorRecord | undefined,
  userId: string,
): TutorArchiveTarget {
  return {
    userId,
    tutorName: formatTutorDisplayName(record),
    isTutoringInProgress: isTutorTutoringInProgress(record),
  };
}

/** Hide archive when the row is already archived. */
export function canShowArchiveRow(
  status: string | undefined,
  canArchiveRow: boolean | undefined,
): boolean {
  return !!canArchiveRow && status !== 'archived';
}

/** Hide reactivate on pre-registered or non-reactivatable statuses. */
export function canShowReactivateRow(
  status: string | undefined,
  tab: string,
  canReactivateRow: boolean | undefined,
): boolean {
  return !!canReactivateRow && tab !== 'pre_registered' && isReactivatableUserStatus(status);
}

/**
 * Normalises a {@link ManagedByValue} filter to the string[] format expected by the API.
 * Returns undefined when no meaningful value is set.
 */
export function buildManagedByParam(
  managedByIds: ManagedByValue | undefined,
): string[] | undefined {
  if (managedByIds === undefined) return undefined;
  if (managedByIds === 'all') return ['all'];
  if (managedByIds === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(managedByIds) && managedByIds.length > 0) return managedByIds;
  return undefined;
}

/** Map UI filters + tab to onboarding list params (omit company/page). */
export function buildTutorRemoteListFilters(
  filters: TutorRemoteFilters,
  activeTab: TutorRemoteTabView,
): Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'> {
  return {
    managedByIds: buildManagedByParam(filters.managedByIds),
    tutorSituation: filters.tutorSituation as TutorSituation | undefined,
    userStatus: TUTOR_REMOTE_TAB_USER_STATUS[activeTab],
    search: filters.search,
    sansRi: filters.sansRi || undefined,
    sansCj: filters.sansCj || undefined,
    activable: filters.activable || undefined,
    registrationDateFrom: filters.registrationDateFrom,
    registrationDateTo: filters.registrationDateTo,
    programIds: (filters.programIds?.length ?? 0) > 0 ? filters.programIds : undefined,
    subject: filters.subject,
    valorisation: filters.valorisation,
    companyName: filters.companyName,
    establishmentName: filters.establishmentName,
    cjValidatedFrom: filters.cjValidatedFrom,
    cjValidatedTo: filters.cjValidatedTo,
    participationRI: filters.participationRI,
    participationStudies: filters.participationStudies,
    stepKeys: filters.stepKeys,
    gender: filters.gender,
    numberOfStudentsToSupport: filters.minCapacity,
    levelIds: filters.levelIds,
  };
}

/** True when any remote-tutor chip / date / search filter is set. */
export function hasTutorRemoteActiveFilters(filters: TutorRemoteFilters): boolean {
  return (
    !!filters.tutorSituation ||
    !!filters.userStatus ||
    !!filters.managedByIds ||
    !!filters.search ||
    !!filters.sansRi ||
    !!filters.sansCj ||
    !!filters.activable ||
    !!filters.registrationDateFrom ||
    !!filters.registrationDateTo ||
    (filters.programIds?.length ?? 0) > 0 ||
    !!filters.subject ||
    !!filters.valorisation ||
    !!filters.companyName ||
    !!filters.establishmentName ||
    !!filters.cjValidatedFrom ||
    !!filters.cjValidatedTo ||
    filters.participationRI !== undefined ||
    filters.participationStudies !== undefined ||
    (filters.stepKeys?.length ?? 0) > 0
  );
}

/**
 * Derives badge configs for status + situation from a tutor record.
 * Shared between {@link TutorRow} and {@link TutorCard}.
 */
export function deriveTutorStatusConfigs(record: TutorRecord): TutorDerivedStatus {
  const { tab, status } = getTutorRemoteBusinessStatus({
    onboardingStatus: record.onboardingStatus,
    folderSituation: record.folderSituation,
    tutorSituation: record.tutorSituation,
    user: record.user,
  });

  const statusConfig =
    tab === 'pre_registered'
      ? TUTOR_PENDING_STATUS_CONFIG[status as keyof typeof TUTOR_PENDING_STATUS_CONFIG]
      : TUTOR_ACTIVE_STATUS_CONFIG[status as keyof typeof TUTOR_ACTIVE_STATUS_CONFIG];

  const situationKey = getTutorSituation({
    onboardingStatus: record.onboardingStatus,
    tutorSituation: record.tutorSituation,
  });

  const situationConfig = TUTOR_SITUATION_CONFIG[situationKey] ?? {
    label: situationKey,
    icon: Clock,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--status-pending))',
  };

  return {
    statusConfig,
    StatusIcon: statusConfig?.icon ?? Clock,
    situationConfig,
    SituationIcon: situationConfig.icon,
  };
}

/** Phone vs email from `primaryContactKind`; `N/R` when empty. */
export function deriveTutorContact(record: TutorRecord): TutorPrimaryContact {
  const resolved = resolveUserContacts(
    record.user?.primaryContactKind,
    record.user?.email,
    record.user?.phone,
  );
  return {
    icon: resolved.kind === 'phone' ? Phone : Mail,
    value: resolved.primary?.value ?? 'N/R',
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/** Formats a date string to dd/mm/yyyy, returns '—' for null/undefined. */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Paris short date-time formatter for 360Learning. */
const LEARNING360_DATE_TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Paris',
});

/** Format a 360Learning ISO timestamp, or "Indisponible". */
export function formatLearning360DateTime(value: string | null): string {
  if (!value) return 'Indisponible';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Indisponible';
  return LEARNING360_DATE_TIME_FORMAT.format(date);
}

/** Hours when the session is long, otherwise minutes and seconds. */
export function formatLearning360TimeSpent(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return 'Indisponible';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) {
    return remMinutes > 0 ? `${hours} h ${remMinutes} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} j ${remHours} h` : `${days} j`;
}

/** Days elapsed since `dateStr`, or "—". */
export function daysSince(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const elapsed = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return `${elapsed} jour${elapsed === 1 ? '' : 's'}`;
}

/** Capitalize the first letter, or "—". */
export function capitalizeFirst(str?: string): string {
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Program name/abbreviation, or a short fallback. */
export function getProgramLabel(programId: string, programsList: any[]): string {
  const program = programsList.find((p: any) => p.id === programId);
  return program?.name ?? program?.abbreviation ?? programId.slice(0, 8);
}

/** Comma-separated program names from programInfo, or "—". */
export function formatProgramsText(programInfo: any, programs: any[]): string {
  if (!programInfo?.programs) return '—';
  if (Array.isArray(programInfo.programs)) {
    return programInfo.programs
      .map((p: any) => {
        if (typeof p === 'object') {
          return p?.programName ?? p?.name ?? p?.abbreviation ?? p?.programId ?? '?';
        }
        return getProgramLabel(String(p), programs);
      })
      .join(', ');
  }
  if (typeof programInfo.programs === 'string') {
    return getProgramLabel(programInfo.programs, programs);
  }
  return String(programInfo.programs);
}

/** Partner display name, capitalized, or "—". */
export function formatPartnerName(partner: any): string {
  if (!partner) return '—';
  if (typeof partner === 'object') {
    return capitalizeFirst(partner.name ?? partner.partnerName ?? '—');
  }
  return capitalizeFirst(partner);
}

/** Level display name, or "—". */
export function formatLevelName(level: any): string {
  if (!level) return '—';
  if (typeof level === 'object') {
    return level.levelName ?? level.name ?? '—';
  }
  return level;
}

/** Returns the registration manager's first + last name, or "—". */
export function formatRegistrationManagerName(record: TutorRecord): string {
  const src = record.source ?? record.user?.registrationSource;
  if (src === 'Landing Page' || src === 'self_registration') return '—';
  if (!record.registrationManager) return '—';
  return `${record.registrationManager.firstName} ${record.registrationManager.lastName}`;
}

/** `"zipCode city"` trimmed, or "—". */
export function formatCityInfo(zipCode: string | undefined, city: string | undefined): string {
  if (!zipCode && !city) return '—';
  return `${zipCode ?? ''} ${city ?? ''}`.trim();
}

/** Paired students from tutor `binomes`, de-duplicated by partner id. */
export function getAccompaniedStudents(record: TutorRecord): Array<{
  binomeId: string;
  studentUserId: string;
  name: string;
  program?: string | null;
  managedByName: string;
}> {
  const binomes = (record.user?.binomes ?? []) as TutorPairPartner[];
  const students: Array<{
    binomeId: string;
    studentUserId: string;
    name: string;
    program?: string | null;
    managedByName: string;
  }> = [];
  const seen = new Set<string>();
  for (const b of binomes) {
    if (b.role && b.role !== 'tutor') continue;
    const name = typeof b.partnerName === 'string' ? b.partnerName.trim() : '';
    if (!name) continue;
    const id =
      (typeof b.partnerUserId === 'string' && b.partnerUserId) ||
      (typeof b.id === 'string' && b.id) ||
      name;
    if (seen.has(id)) continue;
    seen.add(id);
    students.push({
      binomeId: b.id,
      studentUserId: id,
      name,
      program: b.program ?? null,
      managedByName: b.managedBy?.fullName?.trim() || '—',
    });
  }
  return students;
}

/** Accept a slot array or `{ weekly: slots }`. */
function unwrapWeeklyAvailability(availability: unknown): unknown {
  if (Array.isArray(availability)) return availability;
  if (availability && typeof availability === 'object' && 'weekly' in availability) {
    return (availability as { weekly?: unknown }).weekly;
  }
  return [];
}

/** Normalize day/start/end aliases onto {@link WeeklyAvailabilitySlot}. */
export function normalizeDetailWeeklyAvailabilitySlots(
  availability: unknown,
): WeeklyAvailabilitySlot[] {
  const raw = unwrapWeeklyAvailability(availability);
  if (!Array.isArray(raw)) return [];
  return raw
    .map(
      (s: {
        day?: number;
        dayOfTheWeek?: number;
        start?: string;
        startTime?: string;
        end?: string;
        endTime?: string;
      }) => ({
        day: s?.day ?? s?.dayOfTheWeek,
        start: s?.start ?? s?.startTime,
        end: s?.end ?? s?.endTime,
      }),
    )
    .filter((s): s is WeeklyAvailabilitySlot => s?.day != null && !!s?.start && !!s?.end);
}

/** Activity-sector label; falls back to tutor field-of-activity, then raw. */
export function getTutorFieldOfActivityLabel(value: string | undefined): string {
  return value
    ? (ACTIVITY_SECTOR_LABEL[value] ?? FIELD_OF_ACTIVITY_TUTOR_LABEL[value] ?? value)
    : '';
}

/** Study-level catalog label, or the raw value. */
export function getTutorStudyLevelLabel(value: string | undefined): string {
  return value ? (STUDY_LEVEL_LABEL[value] ?? value) : '';
}

/** High-school curriculum catalog label, or the raw value. */
export function getTutorHighSchoolCurriculumLabel(value: string | undefined): string {
  if (!value) return '';
  return HIGH_SCHOOL_CURRICULUM_LABEL[value] ?? value;
}

/** Civil-service branch catalog label, or the raw value. */
export function getTutorCivilServiceBranchLabel(value: string | undefined): string {
  if (!value) return '';
  return CIVIL_SERVICE_BRANCH_LABEL[value] ?? value;
}

/** Employer / establishment name from flattened professional situation. */
export function getRemoteTutorProSituationEnterpriseName(proSit: {
  companyName?: string;
  company?: Array<{ employerName?: string; establishmentName?: string }>;
}): string | undefined {
  return (
    proSit.companyName ||
    proSit.company?.[0]?.employerName ||
    proSit.company?.[0]?.establishmentName
  );
}

/** Department name from a code; falls back to the code itself. */
export function formatRemoteTutorProSituationDepartment(
  code: string | undefined,
  departments: Array<{ code: string; name: string }>,
): string | undefined {
  if (!code) return undefined;
  const dept = departments.find((d) => d.code === code);
  return dept?.name ?? code;
}
